export type IcuAcuity = 'Critical' | 'Watch' | 'Stable'
export type DeviceStatus = 'Ventilator' | 'HFNC' | 'Vasopressor' | 'Monitor only' | 'Available' | 'Maintenance'

export interface IcuBed {
  id: string
  bed: string
  patient?: string
  mrn?: string
  diagnosis?: string
  consultant?: string
  acuity: IcuAcuity
  device: DeviceStatus
  deviceDetail: string
  nurse: string
  ratio: string
  observations: { heartRate: string; map: string; spo2: string; respiratory: string }
  alert?: string
}

export interface BloodUnit {
  id: string
  component: 'Packed RBC' | 'FFP' | 'Platelets' | 'Cryoprecipitate'
  group: string
  available: number
  reserved: number
  expiring: number
  expiry: string
}

export interface CrossMatchRequest {
  id: string
  patient: string
  mrn: string
  group: string
  component: string
  units: number
  priority: 'Routine' | 'Urgent' | 'Massive haemorrhage'
  status: 'Sample pending' | 'Testing' | 'Compatible' | 'Issued'
  destination: string
  requested: string
}

export interface AmbulanceUnit {
  id: string
  callSign: string
  crew: string
  type: 'ALS' | 'BLS' | 'Transfer'
  status: 'Inbound' | 'On scene' | 'Available' | 'Cleaning' | 'Maintenance'
  location: string
  eta: string
  case: string
}

export interface DispatchLogEntry {
  id: string
  time: string
  unit: string
  case: string
  priority: 'P1' | 'P2' | 'P3'
  origin: string
  destination: string
  status: 'Inbound' | 'Arrived' | 'Closed'
}

export const icuBeds: IcuBed[] = [
  { id: 'icu-1', bed: 'ICU-01', patient: 'Rashid Khan', mrn: 'MRN-0047712', diagnosis: 'Septic shock · A41.9', consultant: 'Dr. Adeel Farooq', acuity: 'Critical', device: 'Vasopressor', deviceDetail: 'Noradrenaline 0.18 mcg/kg/min', nurse: 'S. Iqbal', ratio: '1:1', observations: { heartRate: '118', map: '61', spo2: '94%', respiratory: '24' }, alert: 'MAP below target' },
  { id: 'icu-2', bed: 'ICU-02', patient: 'Nusrat Bibi', mrn: 'MRN-0051028', diagnosis: 'ARDS · J80', consultant: 'Dr. Kamran Ali', acuity: 'Critical', device: 'Ventilator', deviceDetail: 'VCV · FiO₂ 65% · PEEP 10', nurse: 'H. Noor', ratio: '1:1', observations: { heartRate: '104', map: '74', spo2: '90%', respiratory: '28' }, alert: 'SpO₂ trending down' },
  { id: 'icu-3', bed: 'ICU-03', patient: 'Muhammad Usman', mrn: 'MRN-0039875', diagnosis: 'Acute respiratory failure · J96.00', consultant: 'Dr. Kamran Ali', acuity: 'Critical', device: 'Ventilator', deviceDetail: 'SIMV · FiO₂ 55% · PEEP 8', nurse: 'N. Ahmed', ratio: '1:1', observations: { heartRate: '112', map: '66', spo2: '91%', respiratory: '26' }, alert: 'K⁺ 6.8 read-back due' },
  { id: 'icu-4', bed: 'ICU-04', patient: 'Waqar Shah', mrn: 'MRN-0054601', diagnosis: 'Post-operative observation', consultant: 'Dr. Imran Sethi', acuity: 'Watch', device: 'HFNC', deviceDetail: '45 L/min · FiO₂ 40%', nurse: 'M. Raza', ratio: '1:2', observations: { heartRate: '88', map: '78', spo2: '96%', respiratory: '20' } },
  { id: 'icu-5', bed: 'ICU-05', acuity: 'Stable', device: 'Maintenance', deviceDetail: 'Ventilator functional test', nurse: '—', ratio: '—', observations: { heartRate: '—', map: '—', spo2: '—', respiratory: '—' } },
  { id: 'icu-6', bed: 'ICU-06', acuity: 'Stable', device: 'Available', deviceDetail: 'Staffed and ready', nurse: 'Float pool', ratio: 'Ready', observations: { heartRate: '—', map: '—', spo2: '—', respiratory: '—' } },
]

export const icuTrend = [
  { time: '06:00', heartRate: 108, map: 72, spo2: 94, respiratory: 23 },
  { time: '08:00', heartRate: 112, map: 69, spo2: 93, respiratory: 24 },
  { time: '10:00', heartRate: 115, map: 67, spo2: 92, respiratory: 25 },
  { time: '12:00', heartRate: 112, map: 66, spo2: 91, respiratory: 26 },
  { time: '14:00', heartRate: 118, map: 64, spo2: 91, respiratory: 26 },
]

export const bloodUnits: BloodUnit[] = [
  { id: 'prbc-o-neg', component: 'Packed RBC', group: 'O−', available: 12, reserved: 2, expiring: 1, expiry: '20 Jul' },
  { id: 'prbc-a-pos', component: 'Packed RBC', group: 'A+', available: 24, reserved: 4, expiring: 2, expiry: '21 Jul' },
  { id: 'prbc-b-pos', component: 'Packed RBC', group: 'B+', available: 18, reserved: 3, expiring: 0, expiry: '24 Jul' },
  { id: 'prbc-ab-pos', component: 'Packed RBC', group: 'AB+', available: 7, reserved: 1, expiring: 0, expiry: '25 Jul' },
  { id: 'ffp-ab', component: 'FFP', group: 'AB', available: 8, reserved: 2, expiring: 1, expiry: '20 Jul' },
  { id: 'plt-random', component: 'Platelets', group: 'Random donor', available: 15, reserved: 5, expiring: 4, expiry: '19 Jul' },
  { id: 'cryo', component: 'Cryoprecipitate', group: 'Universal', available: 11, reserved: 0, expiring: 0, expiry: '28 Jul' },
]

export const crossMatchRequests: CrossMatchRequest[] = [
  { id: 'XM-2026-1182', patient: 'Unknown Male #3391', mrn: 'TMP-003391', group: 'Unknown', component: 'Packed RBC', units: 4, priority: 'Massive haemorrhage', status: 'Issued', destination: 'Emergency · Trauma bay', requested: '13:49' },
  { id: 'XM-2026-1187', patient: 'Fatima Zafar', mrn: 'MRN-0043019', group: 'B+', component: 'Packed RBC', units: 2, priority: 'Urgent', status: 'Compatible', destination: 'OT 02', requested: '10:02' },
  { id: 'XM-2026-1191', patient: 'Muhammad Usman', mrn: 'MRN-0039875', group: 'AB+', component: 'FFP', units: 2, priority: 'Urgent', status: 'Testing', destination: 'ICU-03', requested: '14:08' },
  { id: 'XM-2026-1193', patient: 'Rukhsana Bibi', mrn: 'MRN-0049124', group: 'O+', component: 'Packed RBC', units: 2, priority: 'Routine', status: 'Sample pending', destination: 'Emergency · Red zone', requested: '14:16' },
]

export const ambulanceUnits: AmbulanceUnit[] = [
  { id: 'amb-01', callSign: 'CE-ALS 01', crew: 'Paramedic J. Iqbal · EMT R. Ali', type: 'ALS', status: 'Inbound', location: 'Canal Road · 4.2 km', eta: '06 min', case: 'RTA polytrauma · trauma alert' },
  { id: 'amb-02', callSign: 'CE-ALS 02', crew: 'Paramedic S. Khan · RN H. Noor', type: 'ALS', status: 'On scene', location: 'Gulberg · 2.8 km', eta: '12 min', case: 'Suspected stroke · FAST positive' },
  { id: 'amb-03', callSign: 'CE-BLS 03', crew: 'EMT A. Malik · Driver F. Abbas', type: 'BLS', status: 'Available', location: 'Main Campus', eta: 'Ready', case: 'Available for dispatch' },
  { id: 'amb-04', callSign: 'CE-TFR 04', crew: 'RN K. Ahmed · Driver M. Tariq', type: 'Transfer', status: 'Cleaning', location: 'Decontamination bay', eta: '18 min', case: 'Post-transfer turnaround' },
]

export const dispatchLog: DispatchLogEntry[] = [
  { id: 'DSP-1849', time: '13:42', unit: 'CE-ALS 01', case: 'RTA polytrauma · MTP activated', priority: 'P1', origin: 'Canal Road', destination: 'Emergency · Trauma bay', status: 'Inbound' },
  { id: 'DSP-1850', time: '13:55', unit: 'CE-ALS 02', case: 'Acute stroke · FAST positive', priority: 'P1', origin: 'Gulberg III', destination: 'Emergency · Red zone', status: 'Inbound' },
  { id: 'DSP-1846', time: '12:36', unit: 'CE-BLS 03', case: 'Chest pain transfer', priority: 'P2', origin: 'North Clinic', destination: 'Emergency · Cardiac bay', status: 'Arrived' },
  { id: 'DSP-1841', time: '10:18', unit: 'CE-TFR 04', case: 'Stable interfacility transfer', priority: 'P3', origin: 'Main Campus', destination: 'Rehab Centre', status: 'Closed' },
]
